import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useSettingsStore } from 'common/stores';
import { PasswordInput } from 'core/components/form';
import { CircularIndicator } from 'core/components/uikit';
import { DisclosureWithTitle } from 'features/components';

import {
  ChangePasswordFormData,
  changePasswordSchema,
} from './validationSchema';

const defaultValues = {
  oldPassword: '',
  newPassword: '',
  confirmNewPassword: '',
};

const ChangePasswordForm = () => {
  const { t } = useTranslation();

  const {
    reset,
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: yupResolver(changePasswordSchema),
    defaultValues,
    mode: 'all',
  });

  const isButtonDisabled = !isValid || isSubmitting;

  const onSubmit = handleSubmit(async (values) => {
    const { oldPassword, newPassword } = values;
    const { updatePassword } = useSettingsStore.getState();

    const success = await updatePassword({
      oldPassword,
      newPassword,
    });

    if (success) {
      reset(defaultValues);
      enqueueSnackbar(t('pages.settings.changePasswordForm.success'), {
        variant: 'success',
        hideIconVariant: true,
      });
    }
  });

  return (
    <DisclosureWithTitle
      title={<h4>{t('pages.settings.changePasswordForm.title')}</h4>}
      defaultOpen
    >
      <form
        className="flex max-w-full flex-col gap-4 sm:max-w-80"
        onSubmit={onSubmit}
      >
        <PasswordInput
          name="oldPassword"
          control={control}
          componentProps={{
            label: t(
              'pages.settings.changePasswordForm.fields.oldPassword.label'
            ),
          }}
        />
        <PasswordInput
          name="newPassword"
          control={control}
          componentProps={{
            label: t(
              'pages.settings.changePasswordForm.fields.newPassword.label'
            ),
          }}
        />
        <PasswordInput
          name="confirmNewPassword"
          control={control}
          componentProps={{
            label: t(
              'pages.settings.changePasswordForm.fields.confirmNewPassword.label'
            ),
          }}
        />
        <button type="submit" disabled={isButtonDisabled}>
          {isSubmitting ? <CircularIndicator /> : t('common.save')}
        </button>
      </form>
    </DisclosureWithTitle>
  );
};

export default ChangePasswordForm;
